import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiKeysService, { CreateApiKeyData, UpdateApiKeyData } from '@/services/apiKeys.service';

/**
 * Hook to list API keys
 */
export function useApiKeys(organizationId?: string) {
  return useQuery({
    queryKey: ['apiKeys', organizationId],
    queryFn: () => apiKeysService.listApiKeys(organizationId),
  });
}

/**
 * Hook to get single API key
 */
export function useApiKey(id: string, organizationId?: string) {
  return useQuery({
    queryKey: ['apiKey', id, organizationId],
    queryFn: () => apiKeysService.getApiKey(id, organizationId),
    enabled: !!id,
  });
}

/**
 * Hook to generate API key
 */
export function useGenerateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateApiKeyData) => apiKeysService.generateApiKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });
}

/**
 * Hook to update API key
 */
export function useUpdateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApiKeyData }) =>
      apiKeysService.updateApiKey(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', variables.id] });
    },
  });
}

/**
 * Hook to delete API key
 */
export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId?: string }) =>
      apiKeysService.deleteApiKey(id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });
}

/**
 * Hook to get usage statistics
 */
export function useApiKeyUsageStats(id: string, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['apiKeyUsage', id, startDate, endDate],
    queryFn: () => apiKeysService.getUsageStats(id, startDate, endDate),
    enabled: !!id,
  });
}
